import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { AxisDate, AxisDateWithDay, AxisHoursWork } from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import { ShiftTypeString } from "../util/ShiftOptionsSelection";
import DropDownTrustSelection from "../components/DropDownTrustSelection";
import DropDownTimesheetWeekSelection from "../components/DropDownTimesheetWeekSelection";
import UserSelection from "../components/UserSelection";
import { ShiftLink, UserLink } from "../util/Links";
import DropDownFetchCount from "../components/DropDownFetchCount";
import DropDownRoleSelection from "../components/DropDownRoleSelection";
import { PageTitleController } from "../components/PageTitleController";
import { BsFillCalculatorFill } from "react-icons/bs";
import { Container } from "react-bootstrap";
import { InvoiceStatusIcon } from "../components/InvoiceStatusIcon";
import { arrayBufferToBase64Pdf, downloadInvoiceEml } from "../util/InvoiceEmail";

const InvoiceTable = ({ defaultUser = null, rowsOnly = false }) => {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(25);
  const [key, setKey] = useState(rowsOnly ? "ALL" : "CREATED");
  const [timesheetWeek, setTimesheetWeek] = useState(null);
  const [user, setUser] = useState(defaultUser);
  const [trust, setTrust] = useState(null);
  const [role, setRole] = useState(null);
  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState("");
  const [sortKey, setSortKey] = useState("invoiceNumber");
  const [sortDirection, setSortDirection] = useState("asc");
  const [disableClear, setDisableClear] = useState(false);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [bulkEmailProgress, setBulkEmailProgress] = useState(null);
  const [bulkSentLoading, setBulkSentLoading] = useState(false);
  const [maxBulkSelect, setMaxBulkSelect] = useState(5);

  const trustRef = useRef();
  const roleRef = useRef();
  const userRef = useRef();
  const timesheetWeekRef = useRef();
  const fetchCountRef = useRef();

  useEffect(() => {
    if (!rowsOnly) document.title = "Invoices";
  }, [rowsOnly]);

  useEffect(() => {
    setOffset(0);
    setUser(defaultUser);
  }, [defaultUser]);

  useEffect(() => {
    if (rowsOnly && defaultUser == null) return;
    paginateInvoices();
  }, [offset, key, timesheetWeek, user, trust, role, fetchCount, invoiceNumberSearch, sortKey, sortDirection, rowsOnly, defaultUser]);

  const clearFilters = () => {
    setTrust(null);
    setRole(null);
    setUser(null);
    setTimesheetWeek(null);
    setInvoiceNumberSearch("");
    if (trustRef.current) trustRef.current.reset();
    if (roleRef.current) roleRef.current.reset();
    if (userRef.current) userRef.current.reset();
    if (timesheetWeekRef.current) timesheetWeekRef.current.reset();
    if (fetchCountRef.current) fetchCountRef.current.reset();
  };

  const paginateInvoices = async () => {
    try {
      setLoading(true);

      const params = { status: rowsOnly ? "ALL" : key };
      if (!rowsOnly && timesheetWeek !== null) params["timesheetWeek"] = timesheetWeek?.id;
      if (!rowsOnly && trust !== null) params["trust_id"] = trust?.id;
      if (user !== null) params["user_id"] = user?.id;
      if (!rowsOnly && role !== null) params["role"] = role;
      if (!rowsOnly && invoiceNumberSearch.trim()) params["invoice_number"] = invoiceNumberSearch.trim();

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };

      const config = {
        headers: { "content-type": "application/json" },
        params,
      };

      const page = await authAxios().post(
        "/api/backoffice/invoice/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        setDataSource([]);
        setTotalInvoices(0);
        return;
      }

      setDataSource(page.data.content.map((row) => ({ ...row, selected: false })));
      setTotalInvoices(page.data.totalElements);
      setTotalPages(Math.ceil(page.data.totalElements / fetchCount));
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
    setDisableClear(false);
  };

  const isReadyTab = () => !rowsOnly && key === "READY" && trust?.id != null;

  const handleSetSortKey = (k) => {
    setOffset(0);
    if (sortKey === k) {
      setSortDirection(sortDirection === "asc" ? "dsc" : "asc");
      return;
    }
    setSortKey(k);
  };

  const tableHeading = (k, name) => {
    if (k !== sortKey) return name;
    return sortDirection === "dsc" ? `${name} ↓` : `${name} ↑`;
  };

  const selectedRows = dataSource.filter((r) => r.selected);
  const selectedCount = selectedRows.length;
  const allSelected = dataSource.length > 0 && dataSource.every((r) => r.selected);

  const toggleSelected = (index) => {
    const row = dataSource[index];
    if (!row.selected && selectedCount >= maxBulkSelect) return;
    setDataSource(dataSource.map((r, i) =>
      i === index ? { ...r, selected: !r.selected } : r,
    ));
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setDataSource(dataSource.map((r) => ({ ...r, selected: false })));
    } else {
      let count = 0;
      setDataSource(dataSource.map((r) => {
        if (count < maxBulkSelect) { count++; return { ...r, selected: true }; }
        return { ...r, selected: false };
      }));
    }
  };

  const bulkDownloadEmail = async () => {
    try {
      setBulkEmailLoading(true);
      setBulkEmailProgress({ current: 0, total: selectedRows.length });

      const attachments = [];
      for (let i = 0; i < selectedRows.length; i++) {
        setBulkEmailProgress({ current: i + 1, total: selectedRows.length });
        const row = selectedRows[i];
        const invoiceId = row.invoice?.id;
        const resp = await authAxios().get(`/api/backoffice/invoice/${invoiceId}/file`, { responseType: "arraybuffer" });
        const filename = row.invoice?.invoiceNumber ? `${row.invoice.invoiceNumber}.pdf` : `invoice-${invoiceId}.pdf`;
        attachments.push({ filename, base64: arrayBufferToBase64Pdf(resp.data) });
      }

      const invoiceNumbers = selectedRows.map((r) => r.invoice?.invoiceNumber).filter(Boolean).join(", ");
      downloadInvoiceEml({
        to: trust?.invoiceEmailDestination || trust?.email || "",
        subject: `Invoices${invoiceNumbers ? ` — ${invoiceNumbers}` : ""}`,
        body: `Please find attached invoice${selectedRows.length > 1 ? "s" : ""}: ${invoiceNumbers || selectedRows.map((r) => r.invoice?.id).join(", ")}.`,
        attachments,
        filename: `invoices-${trust?.shortName || "bulk"}.eml`,
      });
    } catch (error) {
      console.log(error);
      alert("Failed to build bulk email");
    } finally {
      setBulkEmailLoading(false);
      setBulkEmailProgress(null);
    }
  };

  const bulkMarkSent = async () => {
    try {
      setBulkSentLoading(true);
      for (const row of selectedRows) {
        await authAxios().post(`/api/backoffice/invoice/${row.invoice?.id}/mark_sent?generate_zip=false`);
      }
      const seen = new Set();
      for (const row of selectedRows) {
        const trustId = row?.trust?.id;
        const week = row?.timesheetWeek;
        if (!trustId || !week) continue;
        const key = `${trustId}|${week}`;
        if (seen.has(key)) continue;
        seen.add(key);
        await authAxios().post("/api/backoffice/invoice/zip", null, {
          params: { trust_id: trustId, week_ending: week },
        });
      }
      await paginateInvoices();
    } catch (error) {
      console.log(error);
      alert("Failed to mark invoices as sent");
    } finally {
      setBulkSentLoading(false);
    }
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;
    return (
      <Fragment>
        <Table striped bordered hover style={{ cursor: "pointer" }}>
          <thead>
            <tr>
              {isReadyTab() && (
                <th style={{ width: "36px" }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              <th onClick={() => handleSetSortKey("invoiceNumber")} style={{ cursor: "pointer" }}>
                {tableHeading("invoiceNumber", "Invoice #")}
              </th>
              <th>Reference</th>
              <th>Name</th>
              <th onClick={() => handleSetSortKey("trust.shortName")} style={{ cursor: "pointer" }}>
                {tableHeading("trust.shortName", "Trust")}
              </th>
              <th>Facility</th>
              <th>Ward</th>
              <th>Grade</th>
              <th>Type</th>
              <th>Status</th>
              <th>Date</th>
              <th onClick={() => handleSetSortKey("bookedShift.weekEnding")} style={{ cursor: "pointer" }}>
                {tableHeading("bookedShift.weekEnding", "Ts Week")}
              </th>
              <th>Hours Worked</th>
              <th onClick={() => handleSetSortKey("status")} style={{ cursor: "pointer" }}>
                {tableHeading("status", "Invoice")}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((row, index) => (
              <tr
                key={row?.id}
                onClick={() => navigate(`/invoice/${row?.invoice?.id}`)}
              >
                {isReadyTab() && (
                  <td onClick={(e) => { e.stopPropagation(); toggleSelected(index); }}>
                    <input
                      type="checkbox"
                      checked={row.selected || false}
                      onChange={() => toggleSelected(index)}
                    />
                  </td>
                )}
                <td>{row?.invoice?.invoiceNumber || <span className="text-muted">not-set</span>}</td>
                <td>{ShiftLink(row)}</td>
                <td>{UserLink(row?.user)}</td>
                <td>{row?.trust?.shortName}</td>
                <td>{row?.facility?.name}</td>
                <td>{row?.ward?.identifier}</td>
                <td>{row?.grade}</td>
                <td>{ShiftTypeString(row?.shiftType)}</td>
                <td className={"text-capitalize"}>
                  {row?.status?.toLowerCase()}
                </td>
                <td>{AxisDateWithDay(row?.start)}</td>
                <td>{AxisDate(row?.timesheetWeek)}</td>
                <td>{AxisHoursWork(row?.timesheet?.hoursWorked)}</td>
                <td>
                  <InvoiceStatusIcon status={row?.invoice?.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {dataSource.length}{" "}
            record{dataSource.length === 1 ? "" : "s"} out of {totalInvoices}
          </p>
          <PaginationControl
            page={offset + 1}
            between={3}
            total={totalInvoices}
            limit={fetchCount}
            changePage={(page) => setOffset(page - 1)}
            ellipsis={1}
          />
        </div>
      </Fragment>
    );
  };

  return (
    <div>
      {!rowsOnly && (
        <Fragment>
          <Row>
            <PageTitleController
              title={"Invoices"}
              icon={<BsFillCalculatorFill className={"icon-right-spacing"} />}
            />
          </Row>

          <Tabs
            id="invoice-table-tabs"
            activeKey={key}
            onSelect={(k) => { setOffset(0); setKey(k); }}
            className="mb-3"
          >
            <Tab eventKey="CREATED" title="Created" />
            <Tab eventKey="READY" title="Ready" />
            <Tab eventKey="SENT" title="Sent" />
            <Tab eventKey="ALL" title="All" />
          </Tabs>

          <Container fluid className={"p-0 m-0"}>
            <Row xs={"auto"} className={"p-0 m-0"}>
              <Col xs={"auto"} className={"ps-0"}>
                <DropDownTimesheetWeekSelection
                  ref={timesheetWeekRef}
                  onSelected={setTimesheetWeek}
                  defaultSelect={timesheetWeek}
                  placeholderText="Select Week"
                />
              </Col>
              <Col xs={"auto"}>
                <UserSelection
                  ref={userRef}
                  onSelected={setUser}
                  defaultValue={user}
                  placeholderText="Select User"
                />
              </Col>
              <Col xs={"auto"}>
                <DropDownTrustSelection
                  ref={trustRef}
                  onSelected={setTrust}
                  defaultValue={trust}
                  placeholderText="Select Trust"
                />
              </Col>
              <Col xs={"auto"}>
                <DropDownRoleSelection
                  ref={roleRef}
                  onSelected={(selectedRole) => { setOffset(0); setRole(selectedRole); }}
                  defaultValue={role}
                  placeholderText="Select Role"
                />
              </Col>
              <Col xs={"auto"}>
                <DropDownFetchCount
                  ref={fetchCountRef}
                  onSelected={setFetchCount}
                  defaultSelection={fetchCount}
                  placeholderText="Select Rows"
                />
              </Col>
              <Col xs={"auto"}>
                <Form.Control
                  placeholder="Invoice #"
                  value={invoiceNumberSearch}
                  onChange={(e) => { setOffset(0); setInvoiceNumberSearch(e.target.value); }}
                  style={{ width: "120px" }}
                />
              </Col>
              <Col xs={"auto"}>
                <Button
                  variant={"danger"}
                  className={"clear-filters-btn"}
                  onClick={() => { setDisableClear(true); clearFilters(); }}
                  disabled={disableClear}
                >
                  <strong>Clear Filters</strong>
                </Button>
              </Col>
              {isReadyTab() && (
                <Fragment>
                  {selectedCount > 0 && (
                    <Col xs={"auto"} className="ms-auto d-flex align-items-center gap-1">
                      <Button variant="outline-secondary" size="sm" onClick={() => setMaxBulkSelect((n) => Math.max(1, n - 1))}>−</Button>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        value={maxBulkSelect}
                        onChange={(e) => setMaxBulkSelect(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: "60px", textAlign: "center" }}
                      />
                      <Button variant="outline-secondary" size="sm" onClick={() => setMaxBulkSelect((n) => n + 1)}>+</Button>
                    </Col>
                  )}
                  {selectedCount > 0 && (
                    <Fragment>
                      <Col xs={"auto"} className="d-flex align-items-center">
                        <span className="text-muted">{selectedCount} selected</span>
                      </Col>
                      <Col xs={"auto"}>
                        <Button className={"shift-button-approve icon-text-wrapper"} onClick={bulkDownloadEmail} disabled={bulkEmailLoading}>
                          {bulkEmailLoading && bulkEmailProgress
                            ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{bulkEmailProgress.current} of {bulkEmailProgress.total}</>
                            : "Download Email"}
                        </Button>
                      </Col>
                      <Col xs={"auto"}>
                        <Button className={"shift-button-new-shift icon-text-wrapper"} onClick={bulkMarkSent} disabled={bulkSentLoading}>
                          {bulkSentLoading ? "Marking…" : "Mark as Sent"}
                        </Button>
                      </Col>
                    </Fragment>
                  )}
                </Fragment>
              )}
            </Row>
          </Container>

          <br />
        </Fragment>
      )}

      {SpinnerOrData(loading)}
    </div>
  );
};

export default InvoiceTable;
